import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import {
  ListRequestOfAttributeFilter,
  ListResponseOfAttribute,
  OrganizationAttributeType,
} from "services/KnowfullyAdminClient";
import attributeStore from "stores/attributeStore";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import { AttributeWithId } from "types/sku-related-types";
import { attributeTypeConfigs } from "./CustomAttributeComponents";

type AttributeListProps = {
  organizationAttributeTypeArgument?: OrganizationAttributeType;
};

export const AttributeList = ({
  organizationAttributeTypeArgument,
}: AttributeListProps) => {
  const { formatMessage: t } = useIntl();
  const {
    attributeService,
    attributeCreateUrl,
    attributeDetailsUrl,
    attributesUrl,
  } = useGlobalContext();
  const navigate = useNavigate();
  const { organizationId } = contextStore.useState();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.attributeSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const organizationAttributeType =
    organizationAttributeTypeArgument ??
    attributeStore.useState((s) => s.organizationAttributeType!);

  useTitle(`${organizationAttributeType.displayName} List`);

  const { data, isFetching } = useStrictQuery<ListResponseOfAttribute>(
    [
      "getAttributes",
      organizationAttributeType.id,
      organizationId,
      debouncedSearchTerm,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      attributeService.getAttributes(
        ListRequestOfAttributeFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
            organizationAttributeTypeId: organizationAttributeType.id,
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (organizationAttributeType) {
      attributeStore.update((s) => {
        s.organizationAttributeType = organizationAttributeType;
      });
    }
  }, [organizationAttributeType]);

  const codeConstant =
    organizationAttributeType?.attributeType?.codeConstant?.toLowerCase();

  const attributeConfig = attributeTypeConfigs.find(
    (c) => c.codeConstant === codeConstant,
  );

  const showSequence =
    organizationAttributeType?.attributeType?.hasSequence !== false;

  const defaultCells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "left",
      width: 300,
      type: "text",
      label: t({ id: "valueLabel" }),
    },
    ...(showSequence
      ? [
          {
            accessor: "sequence",
            align: "left" as const,
            width: 10,
            type: "text" as const,
            label: t({ id: "sequenceLabel" }),
          },
        ]
      : []),
  ];

  const cells = attributeConfig?.listColumns ?? defaultCells;

  let attributes = data?.data as AttributeWithId[];

  const onClick = (id: string) => {
    navigate(attributeDetailsUrl(id));
  };

  return (
    <>
      <Container>
        {!isFetching && (
          <RedirectBackButton
            redirectRoute={attributesUrl()}
            labelKey="backToListButtonLabel"
          />
        )}
        <Box mb={3} textAlign="center">
          <Link to={attributeCreateUrl()} style={{ textDecoration: "none" }}>
            <Fab aria-label="add" color="primary">
              <AddIcon />
            </Fab>
          </Link>
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            value={searchTerm ?? ""}
            placeholder={t({ id: "searchPlaceholder" })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      persistanceStore.update((s) => {
                        s.attributeSearchTerm = "";
                      })
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              persistanceStore.update((s) => {
                s.attributeSearchTerm = e.target.value;
              });
            }}
          />
        </Box>

        <LazyTable
          {...{
            rowCount: data?.totalRecords ?? 0,
            rows: attributes ?? [],
            loading: isFetching,
            dense: true,
            title: organizationAttributeType.displayName,
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            onClick,
          }}
        />
      </Container>
    </>
  );
};
